<script setup lang="ts">
import ProgressFrame from '@/modules/commissioning/views/layout/ProgressFrame.vue';
import { useCommissioningDeviceStore } from '@/modules/commissioning/stores/commissioningDeviceStore';
import type { ComponentErrorType } from '@/modules/commissioning/utils/error-constants';
import type { CommissionResult } from '@/modules/commissioning/models/CommissioningDevice';
import { getCommissionResultConfigValues } from '@/modules/commissioning/utils/error-resolver';
import ComErrorIds from '@/modules/commissioning/components/ComErrorIds.vue';
import ComErrorCtaButtons from '@/modules/commissioning/components/ComErrorCtaButtons.vue';
import { ERROR_CTA_BUTTONS } from '@/modules/commissioning/utils/error-constants';

const errorType = 'UnexpectedSolarCurrent' as ComponentErrorType;
const props = defineProps<{
  failIdsFromRoute?: string[]; // For debugging purposes only
}>();

const deviceStore = useCommissioningDeviceStore();
const commissionResult = deviceStore.commissionResultStatus.results ?? ({} as CommissionResult);

/**
 * Get the fail IDs from the commission result
 */
const commissionResultFailIds = getCommissionResultConfigValues(commissionResult, 'failId');
const failIds = props.failIdsFromRoute ?? commissionResultFailIds[errorType] ?? [];

/**
 * Get the CTA buttons from the commission result
 */
const commissionResultCtaButtons = getCommissionResultConfigValues(commissionResult, 'ctaButtons');
const ctaButtons = commissionResultCtaButtons[errorType] ?? [
  ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
  ERROR_CTA_BUTTONS.RECONFIGURE,
  ERROR_CTA_BUTTONS.TRY_AGAIN,
  ERROR_CTA_BUTTONS.CONTACT_SUPPORT,
];
</script>

<template>
  <ProgressFrame
    pageClass="fullfocus-page"
    titleTight
    withBackArrow
    backButtonClass="error-back-button"
  >
    <template #title><div class="error-title">Unexpected solar current</div></template>
    <!-- <template #subTitle>
      <div class="error-sub-title">&nbsp;</div>
    </template> -->

    <div v-if="failIds.length > 0" class="error-fail-ids--wrapper">
      <ComErrorIds :failIds="failIds" unique />
    </div>

    <div class="error-intro">
      <p>
        This indicates the inverter (or other) suddenly started conducting potentially indicating a
        hardware issue.
      </p>
    </div>

    <div class="error-handle-section">
      <ul class="error-bullet-list">
        <li>Retry once more and see if the issue persists</li>
        <li>Isolate inverter and retry again (inverter issue if passes)</li>
        <li>Contact allume support</li>
      </ul>
    </div>

    <ComErrorCtaButtons :ctaButtons="ctaButtons" />
  </ProgressFrame>
</template>

<style lang="less" scoped></style>
